// Import des licenciés (D-079) : logique pure, testée. L'écran lit le fichier (lib/sheet.ts),
// puis : colonnes devinées → valeurs lues et nettoyées → contrôles → rapprochement avec les
// fiches du club → équipes → envoi. La base refait les contrôles (import_licencies).

export type Field = 'licence' | 'last_name' | 'first_name' | 'full_name' | 'birth_date' | 'gender' | 'category'
  | 'email' | 'phone' | 'parent_name' | 'parent_email' | 'parent_phone' | 'team';
export const FIELD_LABEL: Record<Field, string> = {
  licence: 'Numéro de licence', last_name: 'Nom', first_name: 'Prénom', full_name: 'Nom et prénom', birth_date: 'Date de naissance',
  gender: 'Sexe', category: 'Catégorie FFBB', email: 'E-mail', phone: 'Téléphone', parent_name: 'Parent : nom',
  parent_email: 'Parent : e-mail', parent_phone: 'Parent : téléphone', team: 'Équipe',
};
export const FIELDS = Object.keys(FIELD_LABEL) as Field[];

/** Minuscules, sans accents, ponctuation en espaces. */
export const norm = (s: string): string => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
  .replace(/[°º]/g, ' ').replace(/[^a-z0-9@.]+/g, ' ').trim();

const SYN: Record<Field, string[]> = {
  licence: ['n licence', 'no licence', 'num licence', 'numero licence', 'numero de licence', 'licence', 'n de licence', 'id licence', 'licence ffbb', 'n license', 'numero'],
  last_name: ['nom', 'nom de famille', 'nom licencie', 'nom d usage', 'nom usage'],
  first_name: ['prenom', 'prenoms', 'prenom licencie'],
  full_name: ['nom prenom', 'nom et prenom', 'prenom nom', 'licencie', 'nom complet', 'joueur', 'joueuse', 'identite'],
  birth_date: ['date de naissance', 'ne le', 'nee le', 'ne e le', 'naissance', 'date naissance', 'ddn', 'date naiss', 'ne e', 'date de naiss'],
  gender: ['sexe', 'genre', 'civilite', 'h f', 'm f', 'f m', 'g f'],
  category: ['categorie', 'cat', 'categorie ffbb', 'categ', 'categorie d age'],
  email: ['mail', 'email', 'e mail', 'adresse mail', 'adresse e mail', 'courriel', 'mail licencie', 'email licencie', 'adresse electronique'],
  phone: ['telephone', 'tel', 'portable', 'tel portable', 'mobile', 'telephone portable', 'gsm', 'tel mobile', 'n telephone'],
  parent_name: ['representant legal', 'responsable legal', 'parent', 'nom parent', 'nom du representant', 'responsable', 'tuteur', 'representant', 'nom du responsable'],
  parent_email: ['mail parent', 'email parent', 'mail representant', 'mail responsable', 'courriel parent', 'mail representant legal', 'mail des parents', 'email responsable'],
  parent_phone: ['tel parent', 'telephone parent', 'portable parent', 'tel representant', 'tel responsable', 'telephone responsable', 'tel representant legal'],
  team: ['equipe', 'groupe', 'team'],
};

export interface Mapping { col: number; header: string; field: Field | null; sure: boolean; sample: string }

/** Les colonnes devinées : intitulé exact (sûr), puis contenu ; chaque donnée une seule fois. */
export function guessMapping(header: string[], rows: string[][]): Mapping[] {
  const scored: { col: number; field: Field; score: number }[] = [];
  header.forEach((h, col) => {
    const n = norm(h);
    for (const f of FIELDS) {
      for (const s of SYN[f]) {
        const score = n === s ? 100 : n.startsWith(`${s} `) || n.endsWith(` ${s}`) ? 60 : n.includes(s) && s.length > 3 ? 40 : 0;
        if (score) scored.push({ col, field: f, score: score + (f.startsWith('parent') && /parent|represent|respons|tuteur/.test(n) ? 30 : 0) });
      }
    }
    // Par le contenu, quand l'intitulé ne dit rien.
    const vals = rows.map(r => r[col] ?? '').filter(Boolean).slice(0, 30);
    if (vals.length) {
      const share = (re: RegExp) => vals.filter(v => re.test(v)).length / vals.length;
      if (share(/^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i) > .7) scored.push({ col, field: /parent|represent|respons/.test(n) ? 'parent_email' : 'email', score: 30 });
      if (share(/^(\d{1,2}[/.-]\d{1,2}[/.-]\d{2,4}|\d{4}-\d{2}-\d{2}|\d{5})$/) > .7) scored.push({ col, field: 'birth_date', score: 25 });
      if (share(/^[A-Z]{2}\d{6}$/i) > .7) scored.push({ col, field: 'licence', score: 35 });
      if (share(/^(F|M|H|G|Fille|Garçon|Féminin|Masculin)$/i) > .8) scored.push({ col, field: 'gender', score: 30 });
    }
  });
  scored.sort((a, b) => b.score - a.score);
  const byCol = new Map<number, { field: Field; score: number }>(), used = new Set<Field>();
  for (const s of scored) {
    if (byCol.has(s.col) || used.has(s.field)) continue;
    byCol.set(s.col, { field: s.field, score: s.score }); used.add(s.field);
  }
  // Nom et prénom séparés : la colonne « nom et prénom » n'a plus de raison d'être.
  if (used.has('last_name') && used.has('first_name')) for (const [c, v] of byCol) if (v.field === 'full_name') byCol.delete(c);
  return header.map((h, col) => {
    const m = byCol.get(col);
    return { col, header: h || `Colonne ${col + 1}`, field: m?.field ?? null, sure: (m?.score ?? 0) >= 60, sample: rows.find(r => r[col])?.[col] ?? '' };
  });
}

/** La ligne d'intitulés : la première qui ressemble à des intitulés (pas de dates, plusieurs cases). */
export function findHeaderRow(rows: string[][]): number {
  for (let i = 0; i < Math.min(rows.length, 15); i++) {
    const r = rows[i]!.filter(Boolean);
    if (r.length >= 2 && r.filter(c => FIELDS.some(f => SYN[f].includes(norm(c)))).length >= 2) return i;
  }
  return 0;
}

// --- Valeurs ----------------------------------------------------------------------------------
export type DateRead = { iso: string } | { error: 'illisible' | 'impossible' | 'incomplete' } | null;
const pad = (n: number) => String(n).padStart(2, '0');
/** jj/mm/aaaa, j/m/aa, jj-mm-aaaa, jj.mm.aaaa, aaaa-mm-jj, nombre Excel, avec ou sans heure. */
export function parseDate(raw: string, today = new Date()): DateRead {
  const v = raw.trim().replace(/[ T]\d{1,2}:\d{2}(:\d{2})?(\.\d+)?Z?$/, '');
  if (!v) return null;
  let y: number, m: number, d: number;
  let mm: RegExpExecArray | null;
  if ((mm = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/.exec(v))) { y = +mm[1]!; m = +mm[2]!; d = +mm[3]!; }
  else if ((mm = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2}|\d{4})$/.exec(v))) {
    d = +mm[1]!; m = +mm[2]!; y = +mm[3]!;
    if (mm[3]!.length === 2) y += y > today.getFullYear() % 100 ? 1900 : 2000;
  } else if (/^\d{5}(\.\d+)?$/.test(v)) {
    const dt = new Date(Date.UTC(1899, 11, 30) + Math.floor(Number(v)) * 86400000);
    y = dt.getUTCFullYear(); m = dt.getUTCMonth() + 1; d = dt.getUTCDate();
  } else if (/^\d{4}$/.test(v)) return { error: 'incomplete' };
  else return { error: 'illisible' };
  if (m < 1 || m > 12 || d < 1 || d > 31) return { error: 'impossible' };
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (dt.getUTCMonth() !== m - 1) return { error: 'impossible' };
  const iso = `${y}-${pad(m)}-${pad(d)}`;
  const todayIso = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
  if (iso > todayIso || y < 1920) return { error: 'impossible' };
  return { iso };
}

export function parseGender(raw: string): 'F' | 'M' | null | 'unknown' {
  const n = norm(raw);
  if (!n) return null;
  if (/^(f|fille|femme|feminin|mme|madame|mlle|mademoiselle|female)$/.test(n)) return 'F';
  if (/^(m|h|g|garcon|homme|masculin|mr|monsieur|male)$/.test(n)) return 'M';
  return 'unknown';
}

const PARTICLES = new Set(['de', 'du', 'des', 'van', 'von', 'da', 'di', 'del']);
/** « MARTIN » → « Martin », « léa-marie » → « Léa-Marie », « DE LA FONTAINE » → « de La Fontaine ». */
export function tidyName(raw: string, isLast = false): string {
  const words = raw.replace(/\s+/g, ' ').trim().split(' ').filter(Boolean);
  return words.map((w, i) => {
    const lw = w.toLocaleLowerCase('fr');
    if (isLast && i < words.length - 1 && PARTICLES.has(lw)) return lw;
    return lw.replace(/(^|[-'’])(\p{L})/gu, (_m: string, sep: string, ch: string) => sep + ch.toLocaleUpperCase('fr'));
  }).join(' ');
}

/** « MARTIN Léa » (nom en capitales) ou « Léa Martin ». */
export function splitFullName(raw: string): { first: string; last: string } {
  const words = raw.replace(/\s+/g, ' ').trim().split(' ').filter(Boolean);
  const upper = words.filter(w => w.length > 1 && w === w.toLocaleUpperCase('fr') && /\p{L}/u.test(w));
  if (upper.length && upper.length < words.length) {
    return { last: tidyName(upper.join(' '), true), first: tidyName(words.filter(w => !upper.includes(w)).join(' ')) };
  }
  if (words.length < 2) return { first: tidyName(words.join(' ')), last: '' };
  return { first: tidyName(words[0]!), last: tidyName(words.slice(1).join(' '), true) };
}

export const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i;
export function parseEmail(raw: string): { value: string | null; bad: boolean } {
  const v = raw.trim().toLowerCase().replace(/^mailto:/, '');
  if (!v) return { value: null, bad: false };
  return EMAIL_RE.test(v) && v.length <= 120 ? { value: v, bad: false } : { value: null, bad: true };
}

/** Téléphones français remis en forme ; un 0 perdu par Excel (612345678) est rétabli. */
export function parsePhone(raw: string): { value: string | null; bad: boolean } {
  const v = raw.trim();
  if (!v) return { value: null, bad: false };
  let digits = v.replace(/[^\d+]/g, '');
  if (digits.startsWith('0033')) digits = `0${digits.slice(4)}`;
  else if (digits.startsWith('+33')) digits = `0${digits.slice(3)}`;
  if (/^[1-9]\d{8}$/.test(digits)) digits = `0${digits}`;
  if (/^0\d{9}$/.test(digits)) return { value: digits.replace(/(\d{2})(?=\d)/g, '$1 ').trim(), bad: false };
  if (/^\+\d{8,15}$/.test(digits)) return { value: digits, bad: false };
  return { value: null, bad: true };
}

export function parseLicence(raw: string): { value: string | null; bad: boolean } {
  const v = raw.replace(/\s+/g, '').toUpperCase();
  if (!v) return { value: null, bad: false };
  return /^[A-Z0-9]{4,20}$/.test(v) ? { value: v, bad: false } : { value: null, bad: true };
}

export const CATS = ['U7', 'U9', 'U11', 'U13', 'U15', 'U17', 'U18', 'U20', 'Seniors'] as const;
export type Cat = typeof CATS[number];
/** « U 13 », « Moins de 13 », « SE », « Senior » → U13, Seniors. */
export function parseCategory(raw: string): Cat | null {
  const n = norm(raw).replace(/\s+/g, '');
  if (!n) return null;
  const u = /^(?:u|moinsde|m|-)(\d{1,2})/.exec(n);
  if (u) { const c = `U${u[1]}` as Cat; return (CATS as readonly string[]).includes(c) ? c : null; }
  if (/^(se|sen|senior|seniors|sf|sm|sg|seniorf|seniorm)/.test(n)) return 'Seniors';
  return null;
}
/** La catégorie de l'âge (année de fin de saison − année de naissance). */
export function expectedCategory(birthIso: string, seasonEndYear: number): Cat {
  const age = seasonEndYear - Number(birthIso.slice(0, 4));
  return age <= 7 ? 'U7' : age <= 9 ? 'U9' : age <= 11 ? 'U11' : age <= 13 ? 'U13' : age <= 15 ? 'U15' : age <= 17 ? 'U17' : age <= 20 ? 'U20' : 'Seniors';
}
const catRank = (c: Cat) => (c === 'U18' ? CATS.indexOf('U17') + .5 : CATS.indexOf(c));

// --- Lignes -----------------------------------------------------------------------------------
export type Level = 'block' | 'warn' | 'fixed';
export interface Issue { level: Level; code: string; text: string }
export interface Value {
  first_name: string; last_name: string; birth_date: string | null; gender: 'F' | 'M' | null; licence: string | null;
  category: Cat | null; email: string | null; phone: string | null; parent_name: string | null; parent_email: string | null;
  parent_phone: string | null; team_label: string | null;
}
export interface Row { line: number; raw: Partial<Record<Field, string>>; value: Value; issues: Issue[] }

/** Lire une ligne : valeurs nettoyées et remarques. `line` = numéro de ligne dans le fichier. */
export function readRow(line: number, cells: string[], map: Mapping[], seasonEndYear: number, today = new Date()): Row {
  const raw: Partial<Record<Field, string>> = {};
  for (const m of map) if (m.field) raw[m.field] = (cells[m.col] ?? '').trim();
  const issues: Issue[] = [];
  let first = tidyName(raw.first_name ?? ''), last = tidyName(raw.last_name ?? '', true);
  if ((!first || !last) && raw.full_name) { const s = splitFullName(raw.full_name); first ||= s.first; last ||= s.last; }
  if (raw.first_name && raw.first_name !== first) issues.push({ level: 'fixed', code: 'case', text: 'Majuscules remises en forme' });
  if (!first && !last) issues.push({ level: 'block', code: 'noname', text: 'Ni nom ni prénom' });
  else if (!first) issues.push({ level: 'block', code: 'nofirst', text: 'Prénom manquant' });
  const d = parseDate(raw.birth_date ?? '', today);
  let birth: string | null = null;
  if (d && 'iso' in d) birth = d.iso;
  else if (d && d.error === 'impossible') issues.push({ level: 'block', code: 'date', text: `Date de naissance impossible : « ${raw.birth_date} »` });
  else if (d && d.error === 'incomplete') issues.push({ level: 'block', code: 'date', text: `Date de naissance incomplète : « ${raw.birth_date} »` });
  else if (d) issues.push({ level: 'block', code: 'date', text: `Date de naissance illisible : « ${raw.birth_date} »` });
  else if (first) issues.push({ level: 'block', code: 'nodate', text: 'Date de naissance absente' });
  const g = parseGender(raw.gender ?? '');
  if (g === 'unknown') issues.push({ level: 'warn', code: 'gender', text: `Sexe inconnu : « ${raw.gender} »` });
  const lic = parseLicence(raw.licence ?? '');
  if (lic.bad) issues.push({ level: 'block', code: 'licence', text: `Numéro de licence mal formé : « ${raw.licence} »` });
  const em = parseEmail(raw.email ?? ''), pem = parseEmail(raw.parent_email ?? '');
  if (em.bad) issues.push({ level: 'warn', code: 'email', text: `E-mail invalide : « ${raw.email} »` });
  if (pem.bad) issues.push({ level: 'warn', code: 'email', text: `E-mail du parent invalide : « ${raw.parent_email} »` });
  const ph = parsePhone(raw.phone ?? ''), pph = parsePhone(raw.parent_phone ?? '');
  if (ph.bad) issues.push({ level: 'warn', code: 'phone', text: `Téléphone invalide : « ${raw.phone} »` });
  if (pph.bad) issues.push({ level: 'warn', code: 'phone', text: `Téléphone du parent invalide : « ${raw.parent_phone} »` });
  const cat = parseCategory(raw.category ?? '');
  if (birth && cat) {
    const exp = expectedCategory(birth, seasonEndYear);
    if (catRank(cat) > catRank(exp) && !(cat === 'U18' && exp === 'U17') && !(cat === 'U20' && exp === 'Seniors')) {
      issues.push({ level: 'warn', code: 'surclasse', text: `Inscrit en ${cat}, l’âge d’un ${exp} : surclassement ?` });
    } else if (catRank(cat) < catRank(exp) && !(exp === 'U20' && cat === 'U18')) {
      issues.push({ level: 'warn', code: 'souscat', text: `Inscrit en ${cat}, plus âgé que la catégorie (${exp})` });
    }
  }
  const gender = g === 'unknown' ? null : g;
  const value: Value = {
    first_name: first, last_name: last, birth_date: birth, gender, licence: lic.value, category: cat ?? (birth ? expectedCategory(birth, seasonEndYear) : null),
    email: em.value, phone: ph.value, parent_name: raw.parent_name ? tidyName(raw.parent_name) : null, parent_email: pem.value,
    parent_phone: pph.value, team_label: raw.team || null,
  };
  if (birth && seasonEndYear - Number(birth.slice(0, 4)) < 18 && !value.parent_email && !value.parent_phone && !value.email && !value.phone) {
    issues.push({ level: 'warn', code: 'nocontact', text: 'Mineur sans contact : il faudra le compléter pour envoyer l’accès' });
  }
  return { line, raw, value, issues };
}

/** Doublons dans le fichier : même licence, ou mêmes nom, prénom et naissance. La 1re occurrence reste. */
export function markDuplicates(rows: Row[]): void {
  const seen = new Map<string, number>();
  for (const r of rows) {
    if (r.issues.some(i => i.code === 'noname')) continue;
    const keys = [r.value.licence ? `L:${r.value.licence}` : '', r.value.birth_date ? `N:${norm(r.value.last_name)}|${norm(r.value.first_name)}|${r.value.birth_date}` : ''].filter(Boolean);
    const first = keys.map(k => seen.get(k)).find(x => x != null);
    if (first != null) r.issues.push({ level: 'block', code: 'dup', text: `Même personne qu’à la ligne ${first}` });
    else keys.forEach(k => seen.set(k, r.line));
  }
}

// --- Rapprochement avec les fiches du club ----------------------------------------------------
export interface Known {
  person_id: string; first_name: string; last_name: string | null; birth_date: string | null; gender: string | null;
  email: string | null; phone: string | null; parent_name: string | null; parent_email: string | null; parent_phone: string | null;
  licence: string | null; team_ids: string[]; has_account: boolean;
}
export type MatchKind = 'licence' | 'identity' | 'probable';
export interface Match { kind: MatchKind; known: Known; conflicts: (keyof Value)[] }
const CMP: (keyof Value)[] = ['first_name', 'last_name', 'birth_date', 'gender', 'email', 'phone', 'parent_name', 'parent_email', 'parent_phone'];

/** Licence, puis nom + prénom + naissance (accents, casse, inversion) ; « probable » à trancher. */
export function matchRow(v: Value, index: Known[]): Match | null {
  const conflicts = (k: Known) => CMP.filter(f => {
    const a = v[f], b = (k as unknown as Record<string, string | null>)[f];
    return a != null && a !== '' && b != null && b !== '' && norm(String(a)) !== norm(String(b));
  });
  if (v.licence) { const k = index.find(x => x.licence && x.licence.toUpperCase() === v.licence); if (k) return { kind: 'licence', known: k, conflicts: conflicts(k) }; }
  const f = norm(v.first_name), l = norm(v.last_name);
  const same = (k: Known) => {
    const kf = norm(k.first_name), kl = norm(k.last_name ?? '');
    return (kf === f && (kl === l || !kl || !l)) || (kf === l && kl === f);
  };
  if (v.birth_date) {
    const k = index.find(x => x.birth_date === v.birth_date && same(x));
    if (k) return { kind: 'identity', known: k, conflicts: conflicts(k) };
  }
  // Probable : mêmes noms, naissance absente d'un côté ou jour et mois inversés, ou même année.
  const k = index.find(x => same(x) && (!x.birth_date || !v.birth_date || x.birth_date.slice(0, 4) === v.birth_date.slice(0, 4)));
  return k ? { kind: 'probable', known: k, conflicts: conflicts(k) } : null;
}

// --- Équipes ------------------------------------------------------------------------------------
export interface TeamRef { id: string; name: string; category: string | null; gender: 'F' | 'M' | 'X' }
export type TeamChoice = { team: string } | { newKey: string } | null;
/** Équipe proposée : même catégorie et même sexe (ou mixte) ; sinon une équipe à créer. */
export function suggestTeam(v: Value, teams: TeamRef[]): { choice: TeamChoice; label: string } {
  if (v.team_label) {
    const t = teams.find(x => norm(x.name) === norm(v.team_label!));
    if (t) return { choice: { team: t.id }, label: t.name };
  }
  if (!v.category) return { choice: null, label: 'sans équipe' };
  const fits = teams.filter(t => t.category === v.category && (t.gender === 'X' || !v.gender || t.gender === v.gender));
  if (fits.length) return { choice: { team: fits[0]!.id }, label: fits[0]!.name };
  const g = v.gender === 'F' ? 'F' : v.gender === 'M' ? 'M' : 'X';
  return { choice: { newKey: `${v.category}-${g}` }, label: newTeamName(v.category, g) };
}
export const newTeamName = (cat: string, g: 'F' | 'M' | 'X') =>
  ['U7', 'U9', 'U11'].includes(cat) || g === 'X' ? `${cat} mixte` : `${cat} ${g === 'F' ? 'filles' : 'garçons'}`;

// --- Envoi ---------------------------------------------------------------------------------------
export interface Decision { action: 'create' | 'update' | 'skip'; person_id?: string; team: TeamChoice; overwrite: (keyof Value)[]; note?: string }
export function payloadRow(r: Row, d: Decision): Record<string, unknown> {
  if (d.action === 'skip') return { line: r.line, action: 'skip', note: d.note ?? null };
  const v = r.value;
  return {
    line: r.line, action: d.action, person_id: d.person_id ?? null, first_name: v.first_name, last_name: v.last_name || null,
    birth_date: v.birth_date, gender: v.gender, licence: v.licence, email: v.email, phone: v.phone, parent_name: v.parent_name,
    parent_email: v.parent_email, parent_phone: v.parent_phone,
    team: d.team && 'team' in d.team ? d.team.team : d.team && 'newKey' in d.team ? `new:${d.team.newKey}` : null,
    overwrite: d.overwrite, note: d.note ?? null,
  };
}
